import { Alert, Box, Chip, colors, DialogContentText, Divider, Typography } from '@mui/material';
import { FC } from 'react';
import {
  RecordContextProvider,
  ReferenceManyField,
  ReferenceManyFieldProps,
  TextField,
  useListContext,
  useRefresh,
  useTranslate,
} from 'react-admin';

import {
  B2bCompanyPaymentMethodQuery,
  GetB2bCompanyPaymentMethodResponse,
  PaymentMethodStatus,
  PaymentMethodType,
} from '@Common/types/bo/b2b/companies';
import {
  MdlrBankNameFailedTaskRetryReason,
  mdlrBankNameFailedTaskRetryReasons,
} from '@Common/types/bo/tasks/mdlr_bank_name_failed';

import {
  DeleteButtonPropsI,
  PaginationCustomProps,
  ShowFields,
  ShowSection,
} from '@sh4res/bo-common/components';
import { B2bCompanyPaymentMethodUpdate } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/B2bCompanyPaymentMethodUpdate';
import { sharesAccountWithAnotherPaymentMethod } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/payment_method_availability';
import { paymentMethodPaymentTypeChoices } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/type';
import { SelectChipField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { RaSort } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCompanyPaymentMethodsList';

const defaultSort: RaSort<B2bCompanyPaymentMethodQuery> = { field: 'id', order: 'ASC' };

interface MandateDocument {
  id: string;
  mandate_signature_document_url: string;
  mandate_signature_document_name: string;
}
export const B2bCompanyPaymentMethodsList: FC<
  Omit<
    ReferenceManyFieldProps & { paginationProps?: PaginationCustomProps },
    'reference' | 'target' | 'children'
  >
> = (props) => {
  const resourceBasePath = useResourceBasePath();
  if (!props.source) {
    return null;
  }
  return (
    <ReferenceManyField
      reference={`${resourceBasePath}companies/payment_methods`}
      target="b2b_company_id"
      sort={defaultSort}
      data-testid={`${testId}_PaymentMethodsList`}
      {...props}>
      <LinkedPaymentMethods />
    </ReferenceManyField>
  );
};

const LinkedPaymentMethods: FC = () => {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const refresh = useRefresh();
  const { data: paymentMethods, isLoading } =
    useListContext<GetB2bCompanyPaymentMethodResponse[0]>();
  if (isLoading) {
    return null;
  }

  const deleteConfirmContent =
    'resources.bo_common.b2b.companies.payment_methods.delete_confirm.content';

  const sharedAccountDeleteConfirmContent = (
    <>
      <DialogContentText>{translate(deleteConfirmContent)}</DialogContentText>
      <Alert severity="info" sx={{ mt: 2 }}>
        {translate(
          'resources.bo_common.b2b.companies.payment_methods.delete_confirm.shared_account_info',
        )}
      </Alert>
    </>
  );

  const deleteProps: DeleteButtonPropsI = {
    confirmTitle: 'resources.bo_common.b2b.companies.payment_methods.delete_confirm.title',
    confirmContent: deleteConfirmContent,
    confirmColor: 'warning',
    mutationMode: 'pessimistic',
    redirect: false,
    permissions: { resource: `${resourceBasePath}companies/payment_methods`, action: 'delete' },
    mutationOptions: {
      onSuccess: () => {
        refresh();
      },
    },
    testId: `${testId}_DeleteButton`,
  };

  return (
    <Box>
      {paymentMethods.length === 0 ? (
        <ShowSection label="resources.bo_common.b2b.companies.payment_methods.sections.empty.title">
          <Divider />
          <Typography variant="body1" sx={{ margin: '16px 20px' }}>
            {translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.empty.no_payment_methods',
            )}
          </Typography>
        </ShowSection>
      ) : (
        paymentMethods.map((paymentMethod) => (
          <RecordContextProvider value={paymentMethod} key={`PaymentMethods-${paymentMethod.id}`}>
            <ShowSection
              label={`resources.bo_common.b2b.companies.payment_methods.items.${paymentMethod.type}.${paymentMethod.intent}.title`}
              additionalButtons={
                paymentMethod.retry_reason ? (
                  <B2bCompanyPaymentMethodUpdate paymentMethod={paymentMethod} />
                ) : undefined
              }
              withDelete={{
                ...deleteProps,
                ...(sharesAccountWithAnotherPaymentMethod(paymentMethod, paymentMethods) && {
                  confirmContent: sharedAccountDeleteConfirmContent,
                }),
              }}>
              <PaymentMethodFields
                type={paymentMethod.type}
                retry_reason={paymentMethod.retry_reason}
                status={paymentMethod.status}
                retry_comment={paymentMethod.retry_comment}
              />
            </ShowSection>
          </RecordContextProvider>
        ))
      )}
    </Box>
  );
};

function PaymentMethodFields({
  type,
  retry_reason,
  retry_comment,
  status,
}: {
  type: PaymentMethodType;
  retry_comment: string | null;
  retry_reason: string | null;
  status: PaymentMethodStatus;
}) {
  const commonFields = {
    status: (
      <StatusField
        retry_reason={retry_reason}
        status={status}
        data-testid={`${testId}_StatusField`}
      />
    ),
    ...(!!retry_comment && {
      retry_comment: <TextField emptyText="-" data-testid={`${testId}_RetryCommentField`} />,
    }),
    bank_name: <TextField emptyText="-" data-testid={`${testId}_BankNameField`} />,
    iban: <TextField emptyText="-" data-testid={`${testId}_IbanField`} />,
    beneficiary_name: <TextField emptyText="-" data-testid={`${testId}_BeneficiaryNameField`} />,
  };

  const sepaFields = {
    ...commonFields,
    created_at: <TextField emptyText="-" data-testid={`${testId}_CreatedAtField`} />,
    updated_at: <TextField emptyText="-" data-testid={`${testId}_UpdatedAtField`} />,
    payment_type: (
      <SelectChipField
        choices={paymentMethodPaymentTypeChoices}
        source=""
        data-testid={`${testId}_PaymentTypeField`}
      />
    ),
    mandate_unique_reference: (
      <TextField emptyText="-" data-testid={`${testId}_MandateUniqueReferenceField`} />
    ),
    mandate_singed_at: <TextField emptyText="-" data-testid={`${testId}_MandateSingedAtField`} />,
    mandate_signature_document_url: (
      <DocumentListViewerLink<MandateDocument>
        urlSource="mandate_signature_document_url"
        nameSource="mandate_signature_document_name"
        getFileUrl={() => (record: MandateDocument) => record.mandate_signature_document_url}
      />
    ),
  };

  const externalBankFields = commonFields;

  return (
    <ShowFields<GetB2bCompanyPaymentMethodResponse[0]>
      labelPrefix="resources.bo_common.b2b.companies.payment_methods.fields"
      fields={type === 'external_bank' ? externalBankFields : sepaFields}
    />
  );
}

const colorsMap: Record<PaymentMethodStatus, { backgroundColor?: string; color?: string }> = {
  pending: {},
  awaiting_customer: {},
  active: { backgroundColor: colors.green[500], color: 'white' },
  deleted: { backgroundColor: colors.red[700], color: 'white' },
  rejected: { backgroundColor: colors.red[700], color: 'white' },
  awaiting_signature: {},
};

const StatusField = ({
  retry_reason,
  status,
  'data-testid': dataTestId,
}: {
  status: PaymentMethodStatus;
  retry_reason: string | null;
  'data-testid'?: string;
}) => {
  const translate = useTranslate();
  const isRetry =
    !!retry_reason &&
    mdlrBankNameFailedTaskRetryReasons.includes(retry_reason as MdlrBankNameFailedTaskRetryReason);

  return (
    <Chip
      label={translate(
        isRetry
          ? `resources.bo_common.b2b.companies.payment_methods.choices.retry_reasons.${retry_reason}`
          : `resources.bo_common.b2b.companies.payment_methods.choices.statuses.${status}`,
      )}
      sx={{
        width: 'fit-content',
        ...(isRetry ? { backgroundColor: colors.orange[500], color: 'white' } : colorsMap[status]),
      }}
      data-testid={dataTestId}
    />
  );
};
